import { GameAssetVariant } from '../models/db/tables'
import { GameId } from '../models/game'
import { STACKABLE_ASSET_CONFIGURATION } from '../models/imported/productConfiguration'
import { isDefined } from './isDefined'

export type AssetTarget =
  | 'still'
  | 'safariStill'
  | 'animated'
  | 'animatedSafari'
  | 'animatedHeader'
  | 'animatedHeaderSafari'

const ROYALE_EXTRA_MODIFIERS = ['Card', 'Shard']

const determineAsset = (stackModifier?: string, extraModifier?: string) => (variants: GameAssetVariant[]) => {
  const variant = variants.find((asset) => {
    let matchesModifiers = true

    if (stackModifier) {
      matchesModifiers &&= asset.traits?.stackableAssetModifier === stackModifier
    }

    if (extraModifier && ROYALE_EXTRA_MODIFIERS.includes(extraModifier)) {
      matchesModifiers &&= asset.traits?.extraModifier === extraModifier
    }

    return matchesModifiers
  })

  return variant
    ? {
        path: variant.fileKey,
        mimeType: variant.mimeType,
      }
    : undefined
}

export function getAssetVariantForTarget(
  variants: GameAssetVariant[],
  target: AssetTarget,
  stackModifier?: string,
  extraModifier?: string,
) {
  const resolveAssetFromOptions = determineAsset(stackModifier, extraModifier)

  const baseVariants = variants.filter(
    (variant) => !isDefined(variant.traits?.animatedHeaderAsset) || !variant.traits?.animatedHeaderAsset,
  )
  const headerVariants = variants.filter((variant) => variant.traits?.animatedHeaderAsset)

  switch (target) {
    case 'still':
      const images = baseVariants.filter((variant) => variant.mimeType.startsWith('image/'))
      return resolveAssetFromOptions(images)
    case 'safariStill':
      const safariImages = baseVariants.filter(
        (variant) => variant.mimeType.startsWith('image/') && variant?.traits?.safariFriendly,
      )
      return resolveAssetFromOptions(safariImages)
    case 'animated':
    case 'animatedSafari':
      const webmVideos = baseVariants.filter((variant) => variant.mimeType.startsWith('video/webm'))
      const safariAnimatedVideos = baseVariants.filter(
        (variant) => variant.mimeType.startsWith('video/') && variant?.traits?.safariFriendly,
      )
      const animatedWebPs = baseVariants.filter((variant) => variant.mimeType.startsWith('image/webp'))
      const animatedGifs = baseVariants.filter((variant) => variant.mimeType.startsWith('image/gif'))
      const animatedApngs = baseVariants.filter((variant) => variant.mimeType.startsWith('image/apng'))

      const video =
        target === 'animatedSafari'
          ? resolveAssetFromOptions(safariAnimatedVideos)
          : resolveAssetFromOptions(webmVideos)

      // Prefer videos over other formats
      // Use various animated image formats as fallbacks
      return (
        video ||
        resolveAssetFromOptions(animatedWebPs) ||
        resolveAssetFromOptions(animatedApngs) ||
        resolveAssetFromOptions(animatedGifs)
      )
    case 'animatedHeader':
      const headerAsset = headerVariants
      return resolveAssetFromOptions(headerAsset)
    case 'animatedHeaderSafari':
      const headerSafariAsset = headerVariants.filter(
        (variant) => variant.traits?.animatedHeaderAsset && variant?.traits?.safariFriendly,
      )
      return resolveAssetFromOptions(headerSafariAsset)
  }
}

export const assetVariantForTarget =
  (variants: GameAssetVariant[], stackModifier?: string, extraModifier?: string) => (target: AssetTarget) =>
    getAssetVariantForTarget(variants, target, stackModifier, extraModifier)

export const getStackModifier = (game: GameId, amount: number, itemType?: string) => {
  if (!itemType) {
    return
  }

  switch (game) {
    case 'brawlstars':
      const limits = STACKABLE_ASSET_CONFIGURATION[game]?.[itemType]

      if (!limits) {
        return
      }

      const modifier = limits.findIndex((cur, idx) => {
        const lo = cur
        const hi = limits[idx + 1] ?? Infinity
        return amount >= lo && amount <= (hi ?? Infinity)
      })
      return modifier?.toString()

    default:
      return
  }
}
